import React from 'react';
import { useTranslation } from 'localization';
import StatusBadgeAtom from '../../../atoms/StatusBadge/StatusBadgeAtom';
import type { StatusBadgeRosterVariant } from '../../../atoms/StatusBadge/StatusBadgeAtom';
import type { FactorRosterMember } from '../../../../types/statusFactors';

/** Which roster variant a member reads as. Hidden wins (no status leaks), then broken, then measured. */
export function rosterStatusVariant(member: FactorRosterMember): StatusBadgeRosterVariant {
  if (member.hidden) return 'hidden';
  if (member.contributionUnavailable) return 'broken';
  return member.reporting && member.status && member.status !== 'unknown' ? 'measured' : 'not-yet-measured';
}

// Measured status labels render as Title Case per the dev handoff (boards 8+10).
const STATUS_FALLBACK: Record<string, string> = {
  on_track: 'On Track',
  at_risk: 'At Risk',
  needs_attention: 'Needs Attention',
  critical: 'Critical',
  blocked: 'Blocked',
  unknown: 'Unknown',
};
const BROKEN_FALLBACK: Record<string, string> = {
  factor_disabled: 'Factor is turned off',
  factor_missing: 'Factor no longer exists',
};

/** The variant a member reads as and its localised label — shared by the STATUS badge and MEMBER's narrow-width dot. */
export function useRosterStatus(member: FactorRosterMember): { variant: StatusBadgeRosterVariant; label: string } {
  const { t } = useTranslation();
  const variant = rosterStatusVariant(member);

  let label: string;
  if (variant === 'hidden') {
    // Lowercase italic treatment per handoff (SCSS applies italic + colour on the --hidden variant).
    label = t('statusFactors.roster.hiddenStatus', 'status hidden');
  } else if (variant === 'broken') {
    const reason = member.contributionUnavailable?.reason ?? 'factor_missing';
    label = t(`statusFactors.roster.brokenReason.${reason}`, BROKEN_FALLBACK[reason] ?? reason);
  } else if (variant === 'measured') {
    label = t(`statusFactors.roster.statusValue.${member.status}`, STATUS_FALLBACK[String(member.status)] ?? String(member.status));
  } else {
    // Lowercase italic per handoff.
    label = t('statusFactors.roster.notYetMeasured', 'not yet measured');
  }
  return { variant, label };
}

const StatusCell: React.FC<{ member: FactorRosterMember }> = ({ member }) => {
  const { variant, label } = useRosterStatus(member);
  return (
    <StatusBadgeAtom
      variant={variant}
      label={label}
      statusValue={member.status ?? undefined}
      tooltip={label}
      data-testid="roster-cell-status"
    />
  );
};

export default StatusCell;
